import {
  DeesElement,
  type TemplateResult,
  customElement,
  html,
  css,
  cssManager,
  state,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import type {
  IBottomBarWidget,
  IBottomBarAction,
  IBottomBarAPI,
} from '../../interfaces/appconfig.js';
import { demoFunc } from './dees-appui-bottombar.demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui-bottombar': DeesAppuiBottombar;
  }
}

@customElement('dees-appui-bottombar')
export class DeesAppuiBottombar extends DeesElement implements IBottomBarAPI {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE PROPERTIES
  @state()
  accessor widgets: IBottomBarWidget[] = [];

  @state()
  accessor actions: IBottomBarAction[] = [];

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        height: 24px;
        flex-shrink: 0;
        user-select: none;
      }

      button { font: inherit; color: inherit; border: 0; background: transparent; }
      button:focus-visible, .widget:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
      .widget:not([role="button"]) { cursor: default; }
      .bottom-bar { overflow-x: auto; }
      .widget, .action-button { flex-shrink: 0; }
      @media (prefers-reduced-motion: reduce) { .spinning { animation: none; } .widget, .action-button { transition: none; } }

      .bottom-bar {
        height: 24px;
        display: flex;
        align-items: center;
        padding: 0 var(--dees-spacing-sm);
        gap: var(--dees-spacing-xs);
        background: var(--dees-appui-chrome-bg);
        border-top: 1px solid var(--dees-color-border-subtle);
        font-size: var(--dees-font-caption2-size);
        color: var(--dees-color-text-muted);
      }

      .widget {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 2px 6px;
        border-radius: var(--dees-radius-xs);
        cursor: pointer;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard), color var(--dees-transition-fast) var(--dees-ease-standard);
        white-space: nowrap;
      }

      .widget:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-secondary);
      }

      .widget dees-icon {
        flex-shrink: 0;
      }

      .widget-separator {
        width: 1px;
        height: 14px;
        background: var(--dees-color-border-default);
        margin: 0 var(--dees-spacing-xs);
      }

      /* Status colors matching dees-workspace-bottombar */
      .widget.active {
        color: var(--dees-color-accent-primary);
      }

      .widget.success {
        color: var(--dees-color-text-success);
      }

      .widget.warning {
        color: var(--dees-color-text-warning);
      }

      .widget.error {
        color: var(--dees-color-text-error);
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .spinning {
        animation: spin 1s linear infinite;
      }

      .spacer {
        flex: 1;
      }

      .action-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: var(--dees-radius-xs);
        cursor: pointer;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-muted);
      }

      .action-button:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-secondary);
      }

      .action-button.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .action-button.disabled:hover {
        background: transparent;
        color: var(--dees-color-text-muted);
      }
      @media (pointer: coarse) {
        :host, .bottom-bar { height: 44px; }
        .action-button, .widget[role="button"] { min-width: 44px; min-height: 44px; }
      }
    `,
  ];

  public render(): TemplateResult {
    const leftWidgets = this.widgets
      .filter(w => w.position !== 'right')
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const rightWidgets = this.widgets
      .filter(w => w.position === 'right')
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const leftActions = this.actions.filter(a => a.position === 'left');
    const rightActions = this.actions.filter(a => a.position !== 'left');

    return html`
      <div class="bottom-bar">
        <!-- Left actions -->
        ${leftActions.map(action => this.renderAction(action))}

        <!-- Left widgets -->
        ${leftWidgets.map((widget, index) => html`
          ${index > 0 || leftActions.length > 0 ? html`<div class="widget-separator"></div>` : ''}
          ${this.renderWidget(widget)}
        `)}

        <div class="spacer"></div>

        <!-- Right widgets -->
        ${rightWidgets.map((widget, index) => html`
          ${this.renderWidget(widget)}
          ${index < rightWidgets.length - 1 || rightActions.length > 0 ? html`<div class="widget-separator"></div>` : ''}
        `)}

        <!-- Right actions -->
        ${rightActions.map(action => this.renderAction(action))}
      </div>
    `;
  }

  private renderWidget(widget: IBottomBarWidget): TemplateResult {
    const statusClass = widget.status && widget.status !== 'idle' ? widget.status : '';
    const iconName = widget.iconName
      ? (widget.iconName.startsWith('lucide:') ? widget.iconName : `lucide:${widget.iconName}`)
      : '';

    return html`
      <div
        class="widget ${statusClass}"
        role=${widget.onClick || widget.contextMenuItems?.length ? 'button' : 'status'}
        tabindex=${widget.onClick || widget.contextMenuItems?.length ? '0' : '-1'}
        title="${widget.tooltip || ''}"
        @click=${() => widget.onClick?.()}
        @keydown=${(e: KeyboardEvent) => {
          if ((e.key === 'Enter' || e.key === ' ') && widget.onClick) { e.preventDefault(); widget.onClick(); }
          if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            e.preventDefault(); const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            void this.handleWidgetContextMenu(new MouseEvent('contextmenu', { clientX: rect.left, clientY: rect.top }), widget);
          }
        }}
        @contextmenu=${(e: MouseEvent) => this.handleWidgetContextMenu(e, widget)}
      >
        ${iconName ? html`
          <dees-icon
            .icon=${iconName}
            iconSize="12"
            class="${widget.loading ? 'spinning' : ''}"
          ></dees-icon>
        ` : ''}
        ${widget.label ? html`<span>${widget.label}</span>` : ''}
      </div>
    `;
  }

  private renderAction(action: IBottomBarAction): TemplateResult {
    const iconName = action.iconName.startsWith('lucide:')
      ? action.iconName
      : `lucide:${action.iconName}`;

    return html`
      <button type="button"
        class="action-button ${action.disabled ? 'disabled' : ''}"
        ?disabled=${action.disabled}
        aria-label=${action.tooltip || action.id}
        title="${action.tooltip || ''}"
        @click=${() => !action.disabled && action.onClick?.()}
      >
        <dees-icon
          .icon=${iconName}
          iconSize="12"
        ></dees-icon>
      </button>
    `;
  }

  private async handleWidgetContextMenu(e: MouseEvent, widget: IBottomBarWidget): Promise<void> {
    if (!widget.contextMenuItems || widget.contextMenuItems.length === 0) return;

    e.preventDefault();

    const menuItems: Parameters<typeof DeesContextmenu.openContextMenuWithOptions>[1] = [];

    for (const item of widget.contextMenuItems) {
      if (item.divider) {
        menuItems.push({ divider: true });
      } else {
        menuItems.push({
          name: item.name,
          iconName: item.iconName,
          action: async () => { await item.action(); },
          disabled: item.disabled,
        });
      }
    }

    await DeesContextmenu.openContextMenuWithOptions(e, menuItems);
  }

  // ==========================================
  // API METHODS (implements IBottomBarAPI)
  // ==========================================

  /**
   * Add a widget to the bottom bar
   */
  public addWidget(widget: IBottomBarWidget): void {
    // Remove existing widget with same ID if present
    this.widgets = this.widgets.filter(w => w.id !== widget.id);
    this.widgets = [...this.widgets, widget];
  }

  /**
   * Update an existing widget by ID
   */
  public updateWidget(id: string, update: Partial<IBottomBarWidget>): void {
    this.widgets = this.widgets.map(w =>
      w.id === id ? { ...w, ...update } : w
    );
  }

  /**
   * Remove a widget by ID
   */
  public removeWidget(id: string): void {
    this.widgets = this.widgets.filter(w => w.id !== id);
  }

  /**
   * Get a widget by ID
   */
  public getWidget(id: string): IBottomBarWidget | undefined {
    return this.widgets.find(w => w.id === id);
  }

  /**
   * Clear all widgets
   */
  public clearWidgets(): void {
    this.widgets = [];
  }

  /**
   * Add an action button
   */
  public addAction(action: IBottomBarAction): void {
    this.actions = this.actions.filter(a => a.id !== action.id);
    this.actions = [...this.actions, action];
  }

  /**
   * Remove an action by ID
   */
  public removeAction(id: string): void {
    this.actions = this.actions.filter(a => a.id !== id);
  }

  /**
   * Clear all actions
   */
  public clearActions(): void {
    this.actions = [];
  }
}
